import { fg } from '@atlaskit/platform-feature-flags';

import {
	type AIAnswerQueryStreamEarlyStop,
	type AIAnswerQueryStreamEntry,
	type AIAnswerQueryStreamMarkdownFinalPart,
	AIAnswerQueryStreamType,
	NLPSearchErrorState,
	NLPSearchResultFormat,
	type NLPSearchResultSource,
	type NLPSearchType,
} from '../ai-answer-dialog/types';
import { logger } from '../common/utils/logger';
import { SAINFeatureControl } from '../common/utils/sain-feature-control';

import {
	type AgentError,
	type DefinitionAgentResponse,
	isStreamAnswerPart,
	isStreamDefinitionResponse,
	isStreamDynamicLayoutSubAgent,
	isStreamEarlyStop,
	isStreamError,
	isStreamErrorStateQuerySuggestions,
	isStreamFollowUp,
	isStreamGenerationType,
	isStreamSAINResponse,
	isStreamTrace,
	type SAINAgentResponse,
	type StreamDefinitionMessage,
	type StreamError,
	type StreamSAINMessage,
} from './types';

const mapMimeTypeToFormat = (mimeType: string): NLPSearchResultFormat => {
	switch (mimeType) {
		case 'text/markdown':
			return NLPSearchResultFormat.MARKDOWN;
		case 'text/adf':
			return NLPSearchResultFormat.ADF;
		case 'text/json':
			return NLPSearchResultFormat.JSON;
		default:
			return NLPSearchResultFormat.MARKDOWN;
	}
};

const mapErrorTo = (
	error: StreamError | AgentError,
	traceId?: string | null,
): NLPSearchErrorState => {
	const errorMessage =
		'message_template' in error ? error.message_template : error.message.message_template;

	switch (errorMessage) {
		case 'NO_ANSWER_LLM':
			return NLPSearchErrorState.NoAnswer;
		case 'NO_ANSWER_PEOPLE':
			return NLPSearchErrorState.NoAnswerWhoQuestion;
		case 'NO_ANSWER_KEYWORDS':
			return NLPSearchErrorState.NoAnswerKeywords;
		case 'NO_ANSWER_SEARCH_QA_PLUGIN':
			return NLPSearchErrorState.NoAnswerSearchResults;
		case 'ACCEPTABLE_USE_VIOLATIONS':
			return NLPSearchErrorState.AcceptableUseViolation;
		case 'OPENAI_RATE_LIMIT_USER_ABUSE':
			return NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse;
		case 'FEATURE_DISABLED_ON_SITE':
			return NLPSearchErrorState.AIDisabled;
		default:
			logger.error({
				error,
				functionName: 'mapErrorToSain: NetworkError',
				traceId,
			});
			return NLPSearchErrorState.NetworkError;
	}
};

const identityUserCloudARIPrefix = 'ari:cloud:identity::user/';

// SAIN
export const mapSAINMessageToNLPSearchType = (
	message: SAINAgentResponse,
	requestId?: string | null,
): NLPSearchType => {
	// Handle cases where message is directly in root or nested under message.message
	const messageContent = message.message;

	return {
		nlpResult: fg('kd_sain_extract_footnote')
			? cleanSAINAnswerCitations(messageContent.content)
			: messageContent.content,
		uniqueSources: message.sources
			? message.sources.map(
					(source): NLPSearchResultSource => ({
						ari: source.ari,
						id: source.id?.toString(),
						title: source.title,
						product: source.product,
						type: source.type,
						url: source.url,
						lastModified: source.lastModified,
						iconUrl: null,
						spaceName: null,
						spaceUrl: null,
					}),
				)
			: [],
		disclaimer: messageContent.message_metadata?.disclaimer ?? null,
		errorState: null,
		format: mapMimeTypeToFormat(messageContent.content_mime_type),
		extraAPIAnalyticsAttributes: {
			message_id: messageContent.id,
			conversational_channel_id: messageContent.conversation_channel_id,
			experience_id: messageContent.experience_id,
			user_id: messageContent.user_ari?.replace(identityUserCloudARIPrefix, '') ?? null,
			userQuery: message.human_message?.content ?? null,
			requestId: fg('kd_fix_sain_mau_instrumentation')
				? (requestId ?? null)
				: (messageContent.message_metadata?.request_id ?? null),
			answerGenerationModel: message.usage_data ?? null,
			sources: message.sources,
			cacheInfo: messageContent.message_metadata?.global_cache_info ?? null,
			pluginInvocations: messageContent.plugin_invocations,
			rawJson: message,
			...(fg('kd_sain_online_debugger') && {
				minion_outputs: message.message?.minion_outputs,
				traceId: requestId ?? null,
			}),
		},
	};
};

export const mapStreamSAINResponse = (
	response: StreamSAINMessage,
	traceId?: string | null,
):
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| AIAnswerQueryStreamEarlyStop['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  } => {
	if (isStreamError(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: {
				errorState: mapErrorTo(response, traceId),
				errorCode: response.message.status_code,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	if (isStreamGenerationType(response) && SAINFeatureControl.isLuminaEnabled()) {
		return {
			type: AIAnswerQueryStreamType.GenerationType,
			message: {
				answer_generation_type: response.message.answer_generation_type,
			},
		};
	}

	if (isStreamTrace(response)) {
		return {
			type: AIAnswerQueryStreamType.Trace,
			message: response.message.content,
		};
	}

	if (isStreamAnswerPart(response)) {
		return {
			type: AIAnswerQueryStreamType.AnswerPart,
			message: {
				nlpResult: response.message.content,
			},
		};
	}

	if (isStreamSAINResponse(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: mapSAINMessageToNLPSearchType(response.message, traceId),
		};
	}

	if (isStreamFollowUp(response)) {
		return {
			type: 'follow-up',
			queries: response.message.follow_up_queries ?? [],
		};
	}

	if (isStreamErrorStateQuerySuggestions(response)) {
		return {
			type: AIAnswerQueryStreamType.ErrorStateQuerySuggestions,
			suggested_queries: response.message.suggested_queries ?? [],
		};
	}

	if (SAINFeatureControl.isLuminaEnabled()) {
		if (isStreamDynamicLayoutSubAgent(response)) {
			return {
				type: AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage,
				message: response.message,
			};
		}
	}

	if (fg('kd_sain_early_stop_hello_exp_fg') || fg('kd_sain_early_stop_customer_exp_fg')) {
		if (isStreamEarlyStop(response)) {
			const earlyStopSearchResult: NLPSearchType = {
				nlpResult: response.message?.reason ?? '',
				format: null,
				uniqueSources: [],
				errorState: NLPSearchErrorState.EarlyStop,
				disclaimer: null,
			};

			return {
				type: AIAnswerQueryStreamType.EarlyStop,
				message: earlyStopSearchResult,
			};
		}
	}

	return {
		// Don't currently handle other types, such as PLUGIN_INVOCATION
		type: 'other',
	};
};

// DEFINITIONS
export const mapDefinitionMessageToNLPSearchType = (
	message: DefinitionAgentResponse,
): NLPSearchType => {
	return {
		nlpResult: message.content,
		uniqueSources: message.sources
			? message.sources.map(
					(source): NLPSearchResultSource => ({
						ari: source.ari,
						id: source.id?.toString(),
						title: source.title,
						product: source.product,
						type: source.type,
						url: source.url,
						lastModified: source.lastModified,
						iconUrl: null,
						spaceName: null,
						spaceUrl: null,
					}),
				)
			: [],
		disclaimer: null,
		errorState: null,
		format: NLPSearchResultFormat.MARKDOWN,
		extraAPIAnalyticsAttributes: {
			userQuery: message.human_message?.content ?? null,
			answerGenerationModel: message.usage_data ?? null,
			sources: message.sources,
			rawJson: message,
		},
	};
};

export const mapStreamDefinitionResponse = (
	response: StreamDefinitionMessage,
	traceId?: string | null,
):
	| AIAnswerQueryStreamEntry
	| AIAnswerQueryStreamMarkdownFinalPart['value']
	| {
			type: 'other';
	  }
	| {
			type: 'follow-up';
			queries: string[];
	  } => {
	if (isStreamError(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: {
				errorState: mapErrorTo(response, traceId),
				errorCode: response.message.status_code,
				disclaimer: null,
				format: NLPSearchResultFormat.MARKDOWN,
				uniqueSources: [],
			},
		};
	}

	if (isStreamTrace(response)) {
		return {
			type: AIAnswerQueryStreamType.Trace,
			message: response.message.content,
		};
	}

	if (isStreamAnswerPart(response)) {
		return {
			type: AIAnswerQueryStreamType.AnswerPart,
			message: {
				nlpResult: response.message.content,
			},
		};
	}

	if (isStreamDefinitionResponse(response)) {
		return {
			type: AIAnswerQueryStreamType.FinalResponse,
			message: mapDefinitionMessageToNLPSearchType(response.message),
		};
	}

	if (isStreamFollowUp(response)) {
		return {
			type: 'follow-up',
			queries: response.message.follow_up_queries ?? [],
		};
	}

	return {
		// Don't currently handle other types, such as PLUGIN_INVOCATION
		type: 'other',
	};
};

export const cleanSAINAnswerCitations = (result: string) => {
	const removeFootnoteRegex = /\[\^(\d{1,2})\^\]:\s*https?:\/\/.*$/gm;
	const removeNonDigitCitationsRegex = /\[\^([a-zA-Z\-]+)\^\]/g;

	let cleanedAnswer = result.replace(removeFootnoteRegex, '').trim();
	cleanedAnswer = cleanedAnswer.replace(removeNonDigitCitationsRegex, '').trim();

	return cleanedAnswer;
};
